// One queue for all imperative dialogs. The host owns presentation; callers only
// receive a result after an explicit choice (or a safe cancellation on unmount).
export function createAppDialogStore() {
  let sequence = 0;
  let current = null;
  let snapshot = null;
  let hostCount = 0;
  let hostEpoch = 0;
  let hasMounted = false;
  const queue = [];
  const listeners = new Set();

  const cancelValue = (kind) => kind === 'confirm' ? false : kind === 'prompt' ? null : undefined;
  const publish = () => {
    snapshot = current?.dialog ?? null;
    for (const listener of listeners) {
      // A broken subscriber must never strand a destructive-action confirmation.
      try { listener(); } catch { /* Other subscribers still receive the update. */ }
    }
  };
  const settle = (id, value) => {
    if (!current || current.dialog.id !== id) return;
    const completed = current;
    current = queue.shift() ?? null;
    publish();
    completed.resolve(value);
  };
  const cancelAll = () => {
    const pending = current ? [current, ...queue] : [...queue];
    current = null;
    queue.length = 0;
    publish();
    for (const entry of pending) entry.resolve(cancelValue(entry.dialog.kind));
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    getServerSnapshot: () => null,
    request(kind, message, defaultValue = '', options = {}) {
      if (!['confirm', 'prompt', 'alert'].includes(kind)) {
        throw new TypeError('Unsupported app dialog type');
      }
      if (hasMounted && hostCount === 0) return Promise.resolve(cancelValue(kind));
      return new Promise((resolve) => {
        const entry = {
          resolve,
          dialog: Object.freeze({
            id: ++sequence,
            kind,
            message: String(message ?? ''),
            defaultValue: String(defaultValue ?? ''),
            options: Object.freeze({ ...options }),
          }),
        };
        if (current) queue.push(entry);
        else {
          current = entry;
          publish();
        }
      });
    },
    accept(id, value = '') {
      if (!current || current.dialog.id !== id) return;
      const kind = current.dialog.kind;
      settle(id, kind === 'confirm' ? true : kind === 'prompt' ? String(value ?? '') : undefined);
    },
    cancel(id) {
      if (current?.dialog.id === id) settle(id, cancelValue(current.dialog.kind));
    },
    cancelAll,
    mountHost() {
      hasMounted = true;
      hostCount += 1;
      hostEpoch += 1;
      let mounted = true;
      return () => {
        if (!mounted) return;
        mounted = false;
        hostCount -= 1;
        const epoch = ++hostEpoch;
        // StrictMode immediately remounts effects. Only a real unmount cancels
        // pending choices, including queued password prompts and confirmations.
        queueMicrotask(() => {
          if (hostCount === 0 && hostEpoch === epoch) cancelAll();
        });
      };
    },
  };
}

export const appDialogStore = createAppDialogStore();
export const appConfirm = (message, options) => appDialogStore.request('confirm', message, '', options);
export const appPrompt = (message, defaultValue = '', options) => appDialogStore.request('prompt', message, defaultValue, options);
export const appAlert = (message, options) => appDialogStore.request('alert', message, '', options);
